import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import SEO from '../components/seo/SEO';

const SupportContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.6;
  color: var(--text-primary);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const Intro = styled.p`
  margin-bottom: 2rem;
  color: var(--text-secondary);
`;

const ContactCard = styled.div`
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const ContactLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
`;

const ContactValue = styled.a`
  display: inline-block;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding: 0.65rem 1.1rem;
  background-color: #25d366;
  color: #fff !important;
  border-radius: 2rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background-color: #1da851;
    color: #fff !important;
  }
`;

const Section = styled.section`
  margin-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  color: var(--text-secondary);
`;

const TopicList = styled.ul`
  margin: 0 0 1rem 1.25rem;
  color: var(--text-secondary);

  li {
    margin-bottom: 0.5rem;
  }
`;

const HelpLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SupportPage = () => {
  const { t } = useTranslation();
  const topics = t('support.topics', { returnObjects: true });

  return (
    <SupportContainer>
      <SEO
        title={t('support.seoTitle')}
        description={t('support.seoDescription')}
        keywords={t('support.seoKeywords')}
        url="/support"
      />
      <Title>{t('support.title')}</Title>
      <Intro>{t('support.intro')}</Intro>

      <ContactCard>
        <ContactLabel>{t('support.emailLabel')}</ContactLabel>
        <ContactValue href={`mailto:${t('support.emailValue')}`}>
          {t('support.emailValue')}
        </ContactValue>
      </ContactCard>

      <ContactCard>
        <ContactLabel>{t('support.phoneLabel')}</ContactLabel>
        <ContactValue href="tel:+201040895962">{t('support.phoneValue')}</ContactValue>
      </ContactCard>

      <ContactCard>
        <ContactLabel>{t('support.whatsappLabel')}</ContactLabel>
        <WhatsAppButton href="https://wa.me/201040895962" target="_blank" rel="noopener noreferrer">
          {t('support.whatsappCta')}
        </WhatsAppButton>
      </ContactCard>

      <Paragraph>{t('support.responseTime')}</Paragraph>

      <Section>
        <SectionTitle>{t('support.topicsTitle')}</SectionTitle>
        <TopicList>
          {Array.isArray(topics) && topics.map((topic) => <li key={topic}>{topic}</li>)}
        </TopicList>
      </Section>

      <Section>
        <SectionTitle>{t('support.helpLinksTitle')}</SectionTitle>
        <HelpLinks>
          <Link to="/faq">{t('support.faqLink')}</Link>
          <Link to="/privacy">{t('support.privacyLink')}</Link>
          <Link to="/data-deletion">{t('support.dataDeletionLink')}</Link>
        </HelpLinks>
      </Section>
    </SupportContainer>
  );
};

export default SupportPage;
